import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, Copy, WhatsappLogo, PaperPlaneTilt, Gift } from "@phosphor-icons/react";

export default function Referral() {
    const { t, user } = useApp();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/referral/stats");
                setStats(r.data);
            } catch {}
        })();
    }, []);

    if (!stats) return <AppShell><div className="p-10 text-shield-muted">{t("common.loading")}</div></AppShell>;

    const link = stats.share_link;
    const msg = `Ich schütze meinen digitalen Nachlass mit Digital Lega-Shield. Sichere dir deine App: ${link}`;

    const copy = async () => {
        try { await navigator.clipboard.writeText(link); toast.success(t("referral.copied")); } catch {}
    };

    const nativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: "Digital Lega-Shield", text: msg, url: link });
            } catch {}
        } else {
            copy();
        }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10 lg:py-14">
                <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                        <ShareNetwork size={14} weight="fill" aria-hidden="true" /> Empfehlung
                    </div>
                    <h1 className="mt-2 font-heading font-bold text-shield-ink text-3xl sm:text-4xl" data-testid="referral-title">
                        {t("referral.title")}
                    </h1>
                    <p className="mt-2 text-shield-muted max-w-2xl">{t("referral.sub")}</p>
                </div>

                <div className="mt-10 grid md:grid-cols-3 gap-6">
                    <div className="card-soft p-6" data-testid="referral-invited">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">{t("referral.invited")}</div>
                        <div className="mt-1 font-heading font-bold text-shield-forest text-4xl">{stats.referred_count}</div>
                    </div>
                    <div className="card-soft p-6" data-testid="referral-activated">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">{t("referral.activated")}</div>
                        <div className="mt-1 font-heading font-bold text-shield-terracotta text-4xl">{stats.activated_count}</div>
                    </div>
                    <div className="card-soft p-6" data-testid="referral-bonus">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted flex items-center gap-1.5">
                            <Gift size={12} weight="fill" aria-hidden="true" /> {t("referral.bonus")}
                        </div>
                        <div className="mt-1 font-heading font-bold text-shield-forest text-4xl">{stats.bonus_slots}</div>
                    </div>
                </div>

                <div className="mt-10 card-soft p-6 lg:p-8">
                    <div className="text-sm font-semibold text-shield-ink mb-2">Dein persönlicher Link</div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            readOnly value={link}
                            className="flex-1 h-14 rounded-xl border-2 border-shield-line px-4 text-shield-ink bg-shield-sage/30 font-mono text-sm"
                            data-testid="referral-link-input"
                            onFocus={(e) => e.target.select()}
                        />
                        <button
                            onClick={copy}
                            className="inline-flex items-center gap-2 h-14 px-5 pill bg-shield-forest text-white font-semibold hover:bg-shield-forestDeep transition-colors"
                            data-testid="referral-copy-btn"
                        >
                            <Copy size={16} weight="bold" aria-hidden="true" /> Kopieren
                        </button>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={nativeShare}
                            className="inline-flex items-center gap-2 h-12 px-5 pill bg-shield-terracotta text-white font-semibold hover:bg-shield-terracottaDeep transition-colors"
                            data-testid="referral-share-btn"
                        >
                            <PaperPlaneTilt size={16} weight="bold" aria-hidden="true" /> {t("referral.share")}
                        </button>
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent(msg)}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 h-12 px-5 pill bg-white border border-shield-line text-shield-ink font-semibold hover:bg-shield-sage transition-colors"
                            data-testid="referral-wa-btn"
                        >
                            <WhatsappLogo size={16} weight="bold" aria-hidden="true" /> WhatsApp
                        </a>
                        <a
                            href={`sms:?&body=${encodeURIComponent(msg)}`}
                            className="inline-flex items-center gap-2 h-12 px-5 pill bg-white border border-shield-line text-shield-ink font-semibold hover:bg-shield-sage transition-colors"
                            data-testid="referral-sms-btn"
                        >
                            SMS
                        </a>
                    </div>

                    <p className="mt-6 text-sm text-shield-muted leading-relaxed">{t("referral.desc")}</p>
                </div>
            </div>
        </AppShell>
    );
}
